import { Box, Button, Stack, Typography } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CircleMarker,
  MapContainer,
  Marker,
  Polygon,
  Polyline,
  useMap,
  useMapEvents,
  ZoomControl,
} from 'react-leaflet';

import type { FieldSummary } from '@/api/fields';
import { FieldMarkerCluster } from '@/components/Map/FieldMarkerCluster';
import { createScenarioLocationMapIcon } from '@/components/Map/fieldMapIcon';
import { MapBaseLayers } from '@/components/Map/mapLayers';
import {
  FIELD_MARKER_ZOOM,
  MENA_DEFAULT_ZOOM,
  MENA_MAP_CENTER,
} from '@/constants/fieldMap';
import type { LatLng } from '@/utils/fieldCoordinate';
import { parsePointCoordinate } from '@/utils/fieldCoordinate';

export type LatLngTuple = [number, number];

const labeledMarkerSx = {
  '& .field-map-labeled-marker': {
    background: 'transparent',
    border: 'none',
  },
  '& .field-map-labeled-marker__wrap': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 140,
    height: 79,
    pointerEvents: 'auto',
  },
  '& .field-map-labeled-marker__pin': {
    width: 25,
    height: 41,
    display: 'block',
    flexShrink: 0,
  },
  '& .field-map-labeled-marker__label': {
    marginTop: '2px',
    maxWidth: 136,
    maxHeight: 36,
    overflow: 'hidden',
    padding: '2px 6px',
    borderRadius: 4,
    backgroundColor: 'rgba(255,255,255,0.92)',
    border: '1px solid rgba(0,0,0,0.18)',
    color: '#1a1a1a',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.2,
    textAlign: 'center',
    wordBreak: 'break-word',
    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
    userSelect: 'none',
  },
  '& .field-map-labeled-marker--scenario .field-map-labeled-marker__label': {
    backgroundColor: '#d32f2f',
    color: '#fff',
    borderColor: '#8e0000',
  },
  // Vertex hit targets: grab cursor when hovering handles
  '& .leaflet-interactive.train-poly-vertex-path': {
    cursor: 'grab',
  },
} as const;

interface TrainPolygonEditorProps {
  polygon: string | null;
  onChange: (geojson: string | null) => void;
  flyToCoordinate?: string | null;
  readOnly?: boolean;
  mapHeight?: number;
  enabled?: boolean;
  fields?: FieldSummary[];
}

function parsePolygon(geojson: string | null | undefined): LatLngTuple[] {
  if (!geojson?.trim()) return [];
  try {
    const data = JSON.parse(geojson) as {
      type?: string;
      coordinates?: number[][][] | number[][][][];
      geometry?: { type?: string; coordinates?: number[][][] };
    };
    let coords: number[][] | undefined;
    if (data.type === 'Polygon' && Array.isArray(data.coordinates)) {
      coords = data.coordinates[0] as number[][];
    } else if (data.type === 'Feature' && data.geometry?.type === 'Polygon') {
      coords = data.geometry.coordinates?.[0] as number[][];
    }
    if (!coords?.length) return [];
    const pts = coords.map(([lng, lat]) => [lat, lng] as LatLngTuple);
    if (
      pts.length > 1 &&
      pts[0][0] === pts[pts.length - 1][0] &&
      pts[0][1] === pts[pts.length - 1][1]
    ) {
      return pts.slice(0, -1);
    }
    return pts;
  } catch {
    return [];
  }
}

export function buildPolygonGeoJSON(points: LatLngTuple[]): string | null {
  if (points.length < 3) return null;
  const ring = points.map(([lat, lng]) => [lng, lat]);
  ring.push([...ring[0]]);
  return JSON.stringify({ type: 'Polygon', coordinates: [ring] });
}

function ClickAddVertex({
  onAdd,
  onBackgroundClick,
  enabled,
  suppressRef,
  draggingRef,
}: {
  onAdd: (p: LatLng) => void;
  onBackgroundClick: () => void;
  enabled: boolean;
  suppressRef: React.MutableRefObject<boolean>;
  draggingRef: React.MutableRefObject<number | null>;
}) {
  useMapEvents({
    click(e) {
      if (!enabled) return;
      if (draggingRef.current != null) return;
      if (suppressRef.current) {
        suppressRef.current = false;
        return;
      }
      onBackgroundClick();
      onAdd({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

/** Fly to scenario point once when the editor mounts (does not re-run on redraw). */
function InitialFlyTo({ coordinate }: { coordinate?: string | null }) {
  const map = useMap();
  const didFly = useRef(false);
  useEffect(() => {
    if (didFly.current) return;
    const pt = parsePointCoordinate(coordinate);
    if (!pt) return;
    didFly.current = true;
    map.setView([pt.lat, pt.lng], Math.max(map.getZoom(), FIELD_MARKER_ZOOM), { animate: false });
  }, [map, coordinate]);
  return null;
}

function FitPolygon({ points }: { points: LatLngTuple[] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length < 2) return;
    const bounds = L.latLngBounds(points.map(([lat, lng]) => [lat, lng]));
    map.fitBounds(bounds.pad(0.2), { animate: false });
  }, [map, points]);
  return null;
}

/**
 * Manual vertex drag via CircleMarker + map pointer events.
 * Leaflet Marker.draggable + DivIcon is unreliable in Firefox on macOS.
 */
function VertexEditor({
  vertices,
  selectedIndex,
  onSelect,
  onMove,
  onMoveEnd,
  suppressMapClickRef,
  draggingRef,
  verticesRef,
}: {
  vertices: LatLngTuple[];
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  onMove: (index: number, next: LatLngTuple) => void;
  onMoveEnd: (index: number, next: LatLngTuple) => void;
  suppressMapClickRef: React.MutableRefObject<boolean>;
  draggingRef: React.MutableRefObject<number | null>;
  verticesRef: React.MutableRefObject<LatLngTuple[]>;
}) {
  const map = useMap();
  const movedRef = useRef(false);

  const endDrag = useCallback(() => {
    const idx = draggingRef.current;
    if (idx == null) return;
    draggingRef.current = null;
    map.dragging.enable();
    map.touchZoom.enable();
    map.boxZoom.enable();
    map.keyboard.enable();
    map.getContainer().style.cursor = 'crosshair';
    suppressMapClickRef.current = true;
    if (movedRef.current) {
      const pos = verticesRef.current[idx];
      if (pos) onMoveEnd(idx, pos);
    }
    movedRef.current = false;
  }, [draggingRef, map, onMoveEnd, suppressMapClickRef, verticesRef]);

  useMapEvents({
    mousemove(e) {
      const idx = draggingRef.current;
      if (idx == null) return;
      movedRef.current = true;
      onMove(idx, [e.latlng.lat, e.latlng.lng]);
    },
    mouseup() {
      endDrag();
    },
    mouseleave() {
      // Firefox can leave the map pane while dragging; finish on document instead.
    },
  });

  useEffect(() => {
    const onUp = () => endDrag();
    const onMoveWindow = (ev: MouseEvent | TouchEvent) => {
      const idx = draggingRef.current;
      if (idx == null) return;
      if ('touches' in ev && ev.cancelable) {
        ev.preventDefault();
      }
      let clientX: number;
      let clientY: number;
      if ('touches' in ev) {
        if (!ev.touches.length) return;
        clientX = ev.touches[0].clientX;
        clientY = ev.touches[0].clientY;
      } else {
        clientX = ev.clientX;
        clientY = ev.clientY;
      }
      const rect = map.getContainer().getBoundingClientRect();
      const point = L.point(clientX - rect.left, clientY - rect.top);
      const latlng = map.containerPointToLatLng(point);
      movedRef.current = true;
      onMove(idx, [latlng.lat, latlng.lng]);
    };
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchend', onUp);
    document.addEventListener('mousemove', onMoveWindow);
    document.addEventListener('touchmove', onMoveWindow, { passive: false });
    return () => {
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchend', onUp);
      document.removeEventListener('mousemove', onMoveWindow);
      document.removeEventListener('touchmove', onMoveWindow);
    };
  }, [draggingRef, endDrag, map, onMove]);

  const startDrag = (index: number, originalEvent: Event) => {
    L.DomEvent.preventDefault(originalEvent);
    L.DomEvent.stopPropagation(originalEvent);
    suppressMapClickRef.current = true;
    movedRef.current = false;
    draggingRef.current = index;
    onSelect(index);
    map.dragging.disable();
    map.touchZoom.disable();
    map.boxZoom.disable();
    map.keyboard.disable();
    map.getContainer().style.cursor = 'grabbing';
  };

  return (
    <>
      {vertices.map((pos, idx) => {
        const selected = selectedIndex === idx;
        return (
          <CircleMarker
            key={`v-${idx}`}
            center={pos}
            radius={selected ? 9 : 7}
            pathOptions={{
              className: 'train-poly-vertex-path',
              color: '#0d47a1',
              weight: 2,
              fillColor: selected ? '#1565c0' : '#fff',
              fillOpacity: 1,
              bubblingMouseEvents: false,
            }}
            eventHandlers={{
              mousedown(e) {
                startDrag(idx, e.originalEvent);
              },
              touchstart(e) {
                startDrag(idx, e.originalEvent);
              },
              click(e) {
                L.DomEvent.stopPropagation(e.originalEvent);
                suppressMapClickRef.current = true;
                onSelect(idx);
              },
            }}
          />
        );
      })}
    </>
  );
}

export const TrainPolygonEditor: React.FC<TrainPolygonEditorProps> = ({
  polygon,
  onChange,
  flyToCoordinate,
  readOnly = false,
  mapHeight = 520,
  enabled = true,
  fields = [],
}) => {
  const [mapReady, setMapReady] = useState(false);
  const [vertices, setVertices] = useState<LatLngTuple[]>(() => parsePolygon(polygon));
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const suppressMapClickRef = useRef(false);
  const draggingRef = useRef<number | null>(null);
  // Keep latest vertices for drag-end without stale closures
  const verticesRef = useRef(vertices);
  verticesRef.current = vertices;

  useEffect(() => {
    const next = parsePolygon(polygon);
    setVertices(next);
    setSelectedIndex((idx) => (idx != null && idx < next.length ? idx : null));
  }, [polygon]);

  useEffect(() => {
    if (!enabled) {
      setMapReady(false);
      return;
    }
    const t = window.setTimeout(() => setMapReady(true), 50);
    return () => {
      window.clearTimeout(t);
      setMapReady(false);
    };
  }, [enabled]);

  const closed = vertices.length >= 3;
  const scenarioPoint = useMemo(() => parsePointCoordinate(flyToCoordinate), [flyToCoordinate]);
  const scenarioIcon = useMemo(() => createScenarioLocationMapIcon('Scenario'), []);

  const center = useMemo<[number, number]>(() => {
    if (scenarioPoint) return [scenarioPoint.lat, scenarioPoint.lng];
    if (vertices.length) return vertices[0];
    return [MENA_MAP_CENTER.lat, MENA_MAP_CENTER.lng];
  }, [scenarioPoint, vertices]);

  const fieldPoints = useMemo(
    () =>
      fields
        .map((field) => {
          const pt = parsePointCoordinate(field.coordinate);
          if (!pt) return null;
          return { id: field.id, position: pt, data: field };
        })
        .filter((p): p is { id: string; position: LatLng; data: FieldSummary } => p != null),
    [fields],
  );

  const commitVertices = useCallback(
    (next: LatLngTuple[]) => {
      setVertices(next);
      onChange(buildPolygonGeoJSON(next));
    },
    [onChange],
  );

  const handleAdd = (p: LatLng) => {
    if (readOnly) return;
    const next = [...vertices, [p.lat, p.lng] as LatLngTuple];
    commitVertices(next);
    setSelectedIndex(next.length - 1);
  };

  const handleClear = () => {
    setVertices([]);
    setSelectedIndex(null);
    onChange(null);
  };

  const handleUndo = () => {
    const next = vertices.slice(0, -1);
    commitVertices(next);
    setSelectedIndex((idx) => {
      if (idx == null) return null;
      if (idx >= next.length) return next.length ? next.length - 1 : null;
      return idx;
    });
  };

  const handleDeleteSelected = useCallback(() => {
    if (readOnly || selectedIndex == null) return;
    const next = vertices.filter((_, i) => i !== selectedIndex);
    commitVertices(next);
    setSelectedIndex(null);
  }, [commitVertices, readOnly, selectedIndex, vertices]);

  const handleMove = useCallback((index: number, nextPos: LatLngTuple) => {
    setVertices((prev) => {
      const next = [...prev];
      next[index] = nextPos;
      return next;
    });
  }, []);

  const handleMoveEnd = useCallback(
    (index: number, nextPos: LatLngTuple) => {
      setVertices((prev) => {
        const next = [...prev];
        next[index] = nextPos;
        onChange(buildPolygonGeoJSON(next));
        return next;
      });
    },
    [onChange],
  );

  useEffect(() => {
    if (readOnly) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (selectedIndex == null) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        handleDeleteSelected();
      }
      if (e.key === 'Escape') {
        setSelectedIndex(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleDeleteSelected, readOnly, selectedIndex]);

  return (
    <Box>
      {!readOnly && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Crosshair: click map to add vertices. Press and drag a vertex to move it; click to select,
          then Delete to remove. Blue pins are catalogue fields; the red pin is the scenario
          location when set.
        </Typography>
      )}
      {!readOnly && (
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
          <Button size="small" onClick={handleUndo} disabled={!vertices.length}>
            Undo point
          </Button>
          <Button
            size="small"
            color="error"
            onClick={handleDeleteSelected}
            disabled={selectedIndex == null}
          >
            Delete vertex
          </Button>
          <Button size="small" onClick={handleClear} disabled={!vertices.length}>
            Clear polygon
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {vertices.length} point{vertices.length === 1 ? '' : 's'}
            {closed ? ' (closed)' : ''}
            {selectedIndex != null ? ` · selected #${selectedIndex + 1}` : ''}
          </Typography>
        </Stack>
      )}
      <Box
        sx={{
          height: mapHeight,
          borderRadius: 1,
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
          '& .leaflet-container': {
            height: '100%',
            width: '100%',
            zIndex: 0,
            ...(!readOnly ? { cursor: 'crosshair' } : {}),
          },
          ...labeledMarkerSx,
        }}
      >
        {mapReady ? (
          <MapContainer
            center={center}
            zoom={scenarioPoint ? FIELD_MARKER_ZOOM : MENA_DEFAULT_ZOOM}
            minZoom={2}
            maxZoom={14}
            scrollWheelZoom
            zoomControl={false}
            style={{ height: '100%', width: '100%', cursor: readOnly ? undefined : 'crosshair' }}
          >
            <MapBaseLayers />
            <ZoomControl position="bottomright" />
            <InitialFlyTo coordinate={flyToCoordinate} />
            {readOnly && closed && <FitPolygon points={vertices} />}
            {!readOnly && (
              <ClickAddVertex
                onAdd={handleAdd}
                onBackgroundClick={() => setSelectedIndex(null)}
                enabled={!readOnly}
                suppressRef={suppressMapClickRef}
                draggingRef={draggingRef}
              />
            )}
            {fieldPoints.length > 0 && (
              <FieldMarkerCluster
                points={fieldPoints}
                onSelect={() => undefined}
                showLabels
                getLabel={(p) => p.data.name}
              />
            )}
            {scenarioPoint && (
              <Marker
                position={[scenarioPoint.lat, scenarioPoint.lng]}
                icon={scenarioIcon}
                zIndexOffset={20000}
              />
            )}
            {closed ? (
              <Polygon
                positions={vertices}
                pathOptions={{ color: '#1565c0', weight: 2, fillOpacity: 0.12 }}
              />
            ) : vertices.length >= 2 ? (
              <Polyline
                positions={vertices}
                pathOptions={{ color: '#1565c0', weight: 2, dashArray: '4 4' }}
              />
            ) : null}
            {!readOnly ? (
              <VertexEditor
                vertices={vertices}
                selectedIndex={selectedIndex}
                onSelect={setSelectedIndex}
                onMove={handleMove}
                onMoveEnd={handleMoveEnd}
                suppressMapClickRef={suppressMapClickRef}
                draggingRef={draggingRef}
                verticesRef={verticesRef}
              />
            ) : (
              vertices.map((pos, idx) => (
                <CircleMarker
                  key={`vr-${idx}`}
                  center={pos}
                  radius={6}
                  interactive={false}
                  pathOptions={{
                    color: '#0d47a1',
                    weight: 2,
                    fillColor: '#fff',
                    fillOpacity: 1,
                  }}
                />
              ))
            )}
          </MapContainer>
        ) : null}
      </Box>
    </Box>
  );
};
